import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_DOMAIN } from 'consts';
import { currentUserKey } from 'hooks/use-current-user';
import isString from 'lodash/isString';
import { security } from 'shared';

/** GET/POST/DELETE /api/whatsapp/link — the user's WhatsApp number linkage. */
export type WhatsAppLink = {
  linked: boolean;
  /** E.164 digits without '+', as WhatsApp reports it. */
  phone_number: string | null;
  verified_at: string | null;
  /** Six digits while a link is pending; the user sends it from their phone. */
  link_code: string | null;
  link_code_expires_at: string | null;
  bot_phone_number: string;
  /** Same number as E.164 digits without '+', for wa.me links. */
  bot_phone_digits: string;
};

export const WHATSAPP_LINK_KEY = ['whatsapp-link'];

/**
 * WhatsApp click-to-chat URL (https://faq.whatsapp.com/5913398998672934):
 * `https://wa.me/<digits>?text=<url-encoded text>` opens a chat with that
 * number and pre-fills the message. `digits` must be the bare E.164 digits
 * (no '+', leading zeros, dashes or parentheses). On a phone it opens the app;
 * on desktop it opens WhatsApp Desktop or web.whatsapp.com.
 */
export function whatsAppChatUrl (digits: string, text?: string | null): string {
  const base = `https://wa.me/${digits}`;
  return text ? `${base}?text=${encodeURIComponent(text)}` : base;
}

async function authedFetch (path: string, init: globalThis.RequestInit = {}): Promise<Response> {
  const token = await security.getAccessTokenSilently()();
  return fetch(`${API_DOMAIN}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(init.headers || {}),
    },
  });
}

async function readLink (resp: Response, fallback: string): Promise<WhatsAppLink> {
  if (!resp.ok) {
    let message = `${fallback}: ${resp.status}`;
    try {
      const parsed = await resp.json();
      if (parsed && isString(parsed.error)) message = parsed.error;
    } catch {
      /* non-JSON error body */
    }
    throw new Error(message);
  }
  return resp.json();
}

/**
 * Link status. While a code is pending the dialog polls so it flips to
 * "linked" on its own once the code arrives from the phone.
 */
export function useWhatsAppLink (options?: { enabled?: boolean; pollWhilePending?: boolean }) {
  return useQuery({
    queryKey: WHATSAPP_LINK_KEY,
    queryFn: async () => readLink(await authedFetch('/api/whatsapp/link'), 'Failed to load WhatsApp status'),
    enabled: options?.enabled ?? true,
    refetchInterval: (query) => {
      const data = query.state.data;
      return options?.pollWhilePending && data && !data.linked && data.link_code ? 4000 : false;
    },
  });
}

export function useStartWhatsAppLink () {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      readLink(await authedFetch('/api/whatsapp/link', { method: 'POST' }), 'Failed to create link code'),
    onSuccess: (data) => queryClient.setQueryData(WHATSAPP_LINK_KEY, data),
  });
}

export function useUnlinkWhatsApp () {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      readLink(await authedFetch('/api/whatsapp/link', { method: 'DELETE' }), 'Failed to unlink'),
    onSuccess: (data) => {
      queryClient.setQueryData(WHATSAPP_LINK_KEY, data);
      // /api/user/current carries phone_number too.
      queryClient.invalidateQueries({ queryKey: currentUserKey });
    },
  });
}
